import { takeUntil } from 'rxjs/operators';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Injector,
  Input,
  Output,
  OnDestroy,
} from '@angular/core';

import { KeypadDisplay } from '../classes/KeypadDisplay';
import { KeypadConfigType } from '../../../interfaces/key-pad-config.enum';
import { KeyPadConfigService } from '../../../services';
import { KeyPadService } from '../../../services/key-pad/key-pad.service';

import { E2E_ATTRIBUTES } from '@shared/types/enums/e2e-attributes.enum';

import { BaseComponent } from '@components/base-component';

import type {
  KeypadInput,
  KeypadButton,
  KeypadEvent,
  KeypadOutputEvent,
} from '../classes/KeyPad.type';

import type {
  KeyPadConfig,
  KeyPadDisplayOption,
} from '../../../interfaces/key-pad-config.type';
import type { Subscription } from 'rxjs';

// eslint-disable-next-line prefer-arrow-functions/prefer-arrow-functions
function isKeyPadDisplayOption(
  option: KeypadConfigType | KeyPadDisplayOption,
): option is KeyPadDisplayOption {
  return typeof option === 'object';
}

@Component({
  selector: 'keypad',
  templateUrl: './keypad.component.html',
  styleUrls: ['./keypad.component.scss'],
  providers: [KeyPadService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class KeypadComponent extends BaseComponent implements OnDestroy {
  @Input() public errorMessage = '';
  @Input() public groupedErrorMessage = '';
  @Input() public isInvalid = false;
  @Input() public title = '';
  @Input() public label = '';
  @Input() public placeholder: string;
  @Input() public isActive = true;
  @Input() public returnInitialValue = false;
  @Input() public showLoader = false;
  @Input() public loaderMessage = '';
  @Input() public measure = '';
  @Input() public set displays(
    displayOptions: (KeypadConfigType | KeyPadDisplayOption)[],
  ) {
    this.displaysSet = displayOptions.map((option) => {
      const { keypadType, overrideOptions = {} } = isKeyPadDisplayOption(option)
        ? option
        : { keypadType: option };
      const config: KeyPadConfig = {
        ...this.kpConfig.getConfig(keypadType),
        ...overrideOptions,
      };

      return new KeypadDisplay(keypadType, config);
    });

    this.onChangeDisplayType(this.displaysSet[0], true);
  }
  @Input() public set inputValue(keypadInput: KeypadInput) {
    this.updateValue(keypadInput?.value, keypadInput?.enforced);
  }
  @Output() public value: EventEmitter<number | string> = new EventEmitter();

  public config: KeyPadConfig;
  public buttons: KeypadButton[];
  public displaysSet: KeypadDisplay[] = [];
  public isLongInputValue = false;
  public activeDisplayType: KeypadConfigType;

  public E2E_ATTRIBUTES: typeof E2E_ATTRIBUTES = E2E_ATTRIBUTES;
  @HostBinding('attr.e2e-attr')
  public e2eAttr: string = E2E_ATTRIBUTES.KEY_PAD;

  private keypadSubscription: Subscription;
  private longInputSize = 13;

  public constructor(
    private kpConfig: KeyPadConfigService,
    private keyPadService: KeyPadService,
    private changeDetectorRef: ChangeDetectorRef,
    injector: Injector,
  ) {
    super(injector);
  }

  public createKeypad(
    keypadType: KeypadConfigType,
    config?: KeyPadConfig,
  ): void {
    if (this.keypadSubscription) {
      this.keypadSubscription.unsubscribe();
    }

    this.config = config || this.kpConfig.getConfig(keypadType);
    this.createButtons(keypadType);
    this.keyPadService.initializeKeyPad(this.config);

    this.keypadSubscription = this.keyPadService
      .getKeyPadState()
      .pipe(takeUntil(this.dispose$))
      .subscribe(this.onKeypadValue.bind(this));
  }

  public onKeyPadEvent(keyPadEvent: KeypadEvent): void {
    if (!this.isActive) {
      return;
    }
    this.keyPadService.onKeyPadEvent(keyPadEvent);
  }

  public onChangeDisplayType(
    keypadDisplay: KeypadDisplay,
    init: boolean,
  ): void {
    if (!init && this.activeDisplayType === keypadDisplay.keypadType) {
      return;
    }

    this.activeDisplayType = keypadDisplay.keypadType;
    const activeDisplay: KeypadDisplay = this.getActiveDisplay(
      this.activeDisplayType,
    );
    if (!init) {
      activeDisplay.onHold = true;
    }
    this.createKeypad(this.activeDisplayType, keypadDisplay.config);

    if (!init) {
      this.keyPadService.resetInitialValue();
    }

    if (keypadDisplay?.value?.toString()?.length > 0) {
      const value: number = this.keyPadService.getNumberValue(
        keypadDisplay.value,
      );
      this.keyPadService.setKeyPadValue(value);
    }

    activeDisplay.onHold = false;
  }

  public ngOnDestroy(): void {
    this.keyPadService.destroyKeyPad();
    super.ngOnDestroy();
  }

  private onKeypadValue(state: KeypadOutputEvent): void {
    const activeDisplay: KeypadDisplay = this.getActiveDisplay(
      this.activeDisplayType,
    );

    if (activeDisplay.onHold) {
      return;
    }

    activeDisplay.value = state.asString;
    activeDisplay.isDefaultMinValue =
      state.isDefaultMinValue || state.isDefaultValue;
    this.isLongInputValue = activeDisplay.value.length > this.longInputSize;
    this.changeDetectorRef.detectChanges();

    switch (this.activeDisplayType) {
      default: {
        this.value.next(state.asNumber);
        break;
      }
    }
  }

  private getActiveDisplay(keypadType: KeypadConfigType): KeypadDisplay {
    return this.displaysSet.find((d) => d.keypadType === keypadType);
  }

  private createButtons(keypadType: KeypadConfigType) {
    this.buttons = KeyPadService.createButtons(keypadType);
  }

  private updateValue(value: number, enforced: boolean): void {
    if (value?.toString()?.length > 0) {
      this.keyPadService.setKeyPadValue(
        value,
        this.returnInitialValue,
        enforced,
      );
    }
  }
}
